import { env } from '@/config/env'

// Review apps are always listed from the staging admin endpoint, regardless of
// which environment the app itself currently targets.
const REVIEW_APPS_HOST = 'https://api.staging.stylink.tech'

export interface ReviewApp {
  name: string
  branch: string
  comment: string
  urls: {
    admin: string
    api: string
    web: string
  }
}

export interface ReviewAppsResponse {
  data: ReviewApp[]
}

export interface BrandUserCompany {
  type: 'company'
  id: string | number
  name: string
  brand_dashboard_plan: string
}

// A Brand::User as returned by /api/v1/admin/brand_users. Auth internals
// (passwords, reset/sign-in data) are deliberately not exposed by the API.
export interface BrandUser {
  type: 'brand_user'
  id: string | number
  email: string
  first_name: string | null
  last_name: string | null
  full_name: string | null
  role: 'admin' | 'member'
  locale: string | null
  // null marks an invite-pending seed user with a random password — it can't be
  // logged in as, so the dev switch greys these out.
  first_sign_in_at: string | null
  terms_accepted_at: string | null
  company: BrandUserCompany | null
}

export interface BrandUsersResponse {
  data: BrandUser[]
  meta?: {
    more: boolean
  }
}

// The staging admin token reads both ReviewApp and Brand::User on the staging
// host; review apps authenticate with their own seeded token. Review apps are
// all served from *.review.stylink.tech, so anything else (staging + the default
// base) uses the staging token.
function keyForBaseUrl(baseUrl: string): string | undefined {
  return baseUrl.includes('.review.stylink.tech')
    ? env.VITE_REVIEW_APPS_API_KEY_REVIEW
    : env.VITE_REVIEW_APPS_API_KEY
}

// These admin endpoints authenticate with a dedicated read-only key (baked in at
// build time for staging only), not the user's session token — so they bypass the
// regular apiClient and call fetch directly.
async function adminFetch<T>(url: string, apiKey: string | undefined): Promise<T> {
  if (!apiKey) {
    throw new Error('Admin API key is not configured for this environment')
  }

  let response: Response
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Authorization': `Bearer ${apiKey}`,
      },
    })
  } catch (error) {
    // A CORS rejection or network failure surfaces as a TypeError with no usable
    // detail — give a clear, actionable message instead.
    if (error instanceof TypeError) {
      throw new Error(
        'Could not reach the admin endpoint. This is usually a CORS restriction — ' +
          'the staging API must allow requests from this origin.'
      )
    }
    throw error
  }

  if (!response.ok) {
    const detail = await response.text().catch(() => '')
    throw new Error(
      `Admin request failed: ${String(response.status)} ${response.statusText}. ${detail}`.trim()
    )
  }

  return (await response.json()) as T
}

export function fetchReviewApps(): Promise<ReviewAppsResponse> {
  // The review-apps list always comes from staging → staging token.
  return adminFetch<ReviewAppsResponse>(
    `${REVIEW_APPS_HOST}/api/v1/admin/review_apps`,
    env.VITE_REVIEW_APPS_API_KEY
  )
}

// Lists brand users on the given environment (newest first). Built for the dev
// switch: after picking a review app, list its seeded brand users to log in as.
export function fetchBrandUsers(search: string, baseUrl: string): Promise<BrandUsersResponse> {
  const url = new URL('/api/v1/admin/brand_users', baseUrl)
  if (search) {
    // Ransack predicate: matches the search term against user emails.
    url.searchParams.set('q[email_cont_any]', search)
  }
  return adminFetch<BrandUsersResponse>(url.toString(), keyForBaseUrl(baseUrl))
}
